/**
 * Shared class recipes for the primitives.
 *
 * These live outside the component files so each of those exports components and
 * nothing else — which is what keeps React Fast Refresh able to hot-swap them
 * without losing state (and what the react-refresh lint rule enforces).
 */
import { cn } from '@core/lib/cn';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'md' | 'lg';

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  // Gold fill, near-black label — 11.3:1. On the dark page the fill needs no
  // outline for contrast (gold is 11.3:1 against it), but the inset dark ring
  // stays because it is the logo's own device: every gold shape in the
  // Printoman mark is outlined. It reads as embossed rather than as a fix.
  primary:
    'bg-brand text-brand-contrast ring-1 ring-inset ring-brand-edge ' +
    'hover:bg-brand-strong active:bg-brand-strong shadow-sm',
  secondary:
    'bg-surface text-ink border border-line-strong hover:bg-surface-sunken active:bg-surface-sunken',
  // brand-ink, not brand: on the dark page it is a brighter gold than the
  // primary fill, so a ghost button reads as a link-weight action rather than
  // as a primary button that lost its box.
  ghost: 'bg-transparent text-brand-ink hover:bg-brand-soft active:bg-brand-soft',
  danger: 'bg-danger text-white hover:brightness-95 active:brightness-90 shadow-sm',
};

const BUTTON_SIZES: Record<ButtonSize, string> = {
  // Both sizes clear 44px — WCAG 2.5.5, and the difference between a thumb
  // hitting the button and hitting the link under it.
  md: 'min-h-touch px-4 text-[0.9375rem]',
  lg: 'min-h-[3.25rem] px-5 text-base',
};

/**
 * Button chrome, shared by Button and ButtonLink so the two cannot drift.
 *
 * No hover-only affordances: hover styling is additive, and every state a user
 * needs (focus, disabled, busy) is expressed without a pointer, because touch
 * devices never fire hover.
 */
export function buttonClasses(
  variant: ButtonVariant = 'primary',
  size: ButtonSize = 'md',
  fullWidth = false,
  className?: string,
): string {
  return cn(
    'inline-flex items-center justify-center gap-2 rounded-control font-semibold no-underline',
    'transition-colors duration-150',
    // Stated explicitly rather than relying on the global :focus-visible rule, so
    // a utility class from a caller cannot quietly remove the ring.
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2',
    'disabled:cursor-not-allowed disabled:opacity-60 aria-disabled:cursor-not-allowed aria-disabled:opacity-60',
    // Removes the 300ms double-tap-zoom delay on iOS for primary actions.
    'touch-manipulation select-none',
    BUTTON_VARIANTS[variant],
    BUTTON_SIZES[size],
    fullWidth ? 'w-full' : '',
    className,
  );
}

/** Form-control chrome, shared by Input, Textarea and anything using Field. */
export function controlClasses(invalid: boolean, extra?: string): string {
  return cn(
    'w-full min-h-touch rounded-control border bg-surface px-3 py-2 text-base text-ink',
    'placeholder:text-ink-subtle',
    // 16px text (text-base) is deliberate: anything smaller makes iOS Safari zoom
    // the viewport on focus, which feels broken on a checkout form.
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2',
    'disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:text-ink-subtle',
    invalid ? 'border-danger ring-1 ring-danger/40' : 'border-line-strong',
    extra,
  );
}
